import DOMPurify from 'dompurify';
import React, {useEffect, useState, useCallback} from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import './styles.scss';

interface AuthorizedImageProps {
  source: string;
  className?: any;
  height?: any;
  width?: any;
  alt?: string;
  style?: any;
  onClick?: any;
  onError?: any;
  accessToken: any;
  default?: any;
  requiresAuthorization?: boolean;
  onLoaded?: () => void;
}

function AuthorizedImage({
  source,
  className,
  accessToken,
  requiresAuthorization = true,
  onLoaded,
  ...props
}: AuthorizedImageProps) {
  const [imageSource, setImageSource] = useState<string>('');
  const [svgText, setSvgText] = useState<string>('');
  const [fetchAgain, setFetchAgain] = useState<number>(0);

  const fetchImage = useCallback(
    async (mounted: boolean) => {
      if (
        source.includes('BOnull') ||
        source.includes('BOundefined') ||
        source.includes('object%20Object')
      ) {
        return;
      }
      const access_token = localStorage.getItem('access_token') || accessToken;
      if (access_token && fetchAgain < 2) {
        try {
          const response = await fetch(source, {
            headers: requiresAuthorization
              ? {
                  Authorization: 'Bearer ' + access_token,
                  X_MYADV_API_KEY:
                    process.env.REACT_APP_DEFAULT_TENANT_ALIAS || '',
                }
              : {
                  X_MYADV_API_KEY:
                    process.env.REACT_APP_DEFAULT_TENANT_ALIAS || '',
                },
          });

          if (response.status === 404 || response.status === 401) {
            if (mounted) setFetchAgain(fetchAgain + 1);
            if (fetchAgain >= 1) {
              onLoaded?.();
            }
          } else {
            const contentType = response.headers.get('content-type');
            if (
              !response.ok ||
              !contentType ||
              !contentType.startsWith('image/')
            ) {
              if (mounted) {
                setImageSource(props?.default);
                onLoaded?.();
              }
              return;
            }
            if (source.includes('.svg')) {
              if (mounted) {
                setSvgText(DOMPurify.sanitize(await response.text()));
                onLoaded?.();
              }
            } else {
              await response.blob().then((blobResponse) => {
                if (!mounted) return;
                const urlCreator = window.URL || window.webkitURL;
                setImageSource(urlCreator.createObjectURL(blobResponse));
                onLoaded?.();
              });
            }
          }
        } catch (error: any) {
          if (error?.name === 'AbortError') return;
          console.log('Error fetching image:', error);
          if (mounted) {
            onLoaded?.();
          }
        }
      } else {
        if (mounted) {
          setImageSource(props?.default);
          onLoaded?.();
        }
      }
    },
    [source, fetchAgain, onLoaded],
  );

  const fetchImageNoAuth = useCallback(
    async (mounted: boolean) => {
      if (
        source.includes('BOnull') ||
        source.includes('BOundefined') ||
        source.includes('object%20Object')
      ) {
        return;
      }
      if (fetchAgain < 2) {
        try {
          const response = await fetch(source, {
            headers: {
              X_MYADV_API_KEY: process.env.REACT_APP_DEFAULT_TENANT_ALIAS || '',
            },
          });

          if (response.status === 404 || response.status === 401) {
            if (mounted) setFetchAgain(fetchAgain + 1);
            if (fetchAgain >= 1) {
              onLoaded?.();
            }
          } else {
            const contentType = response.headers.get('content-type');

            if (
              !response.ok ||
              !contentType ||
              !contentType.startsWith('image/')
            ) {
              if (mounted) {
                setImageSource(props?.default);
                onLoaded?.();
              }
              return;
            }
            if (source.includes('.svg')) {
              if (mounted) {
                setSvgText(DOMPurify.sanitize(await response.text()));
                onLoaded?.();
              }
            } else {
              await response.blob().then((blobResponse) => {
                if (!mounted) return;
                const urlCreator = window.URL || window.webkitURL;
                setImageSource(urlCreator.createObjectURL(blobResponse));
                onLoaded?.();
              });
            }
          }
        } catch (error: any) {
          if (error?.name === 'AbortError') return;
          console.log('Error fetching image:', error);
          if (mounted) {
            onLoaded?.();
          }
        }
      } else {
        if (mounted) {
          setImageSource(props?.default);
          onLoaded?.();
        }
      }
    },
    [source, fetchAgain, onLoaded],
  );

  useEffect(() => {
    let mounted = true;

    if (localStorage.getItem('access_token') || accessToken) {
      fetchImage(mounted);
    } else {
      fetchImageNoAuth(mounted);
    }

    return () => {
      mounted = false;
    };
  }, [fetchImage, fetchImageNoAuth]);

  // console.log(
  //   'imgsource (*remove after understanding the cause of imageSource being wrong*):',
  //   imageSource,
  // );

  return (
    <>
      {imageSource && !source.includes('undefined') && (
        <img
          src={imageSource}
          style={props.style}
          className={className ? className : 'app-spinner-default'}
          onError={(err) => {
            console.log('Error loading image:', err.type);
            setFetchAgain(fetchAgain + 1);
            onLoaded?.();
          }}
          {...props}
        />
      )}
      {svgText && (
        <div
          key={svgText}
          className={className}
          style={props.style}
          dangerouslySetInnerHTML={{__html: svgText}}
          {...props}
        />
      )}
    </>
  );
}

const mapStateToProps = ({accessToken}: any) => ({
  accessToken: accessToken.value,
});

export default compose(connect(mapStateToProps))(React.memo(AuthorizedImage));
